import axios from 'axios';

import {
  IBurnConfigUser,
  IBurnPreview,
  IBurnTokenBurner,
  IBurnTokenVolume,
} from '../models/burn/i-burn-token.interface';

// Public read endpoints served by the burn app (see burn/api_urls.py). They mirror
// the sibling asset token endpoints (top_owners / sales_volume) but aggregate over
// SETTLED burn batches. The burn app routes omit the trailing slash, matching its
// existing config/eligible/batches routes.
export const fetchTokenBurners = (id: string): Promise<IBurnTokenBurner[]> => {
  return axios.get<IBurnTokenBurner[]>(`/api/v1/burn/token/${id}/burners`).then(({ data }) => data);
};

export const fetchBurnVolume = (id: string): Promise<IBurnTokenVolume> => {
  return axios.get<IBurnTokenVolume>(`/api/v1/burn/token/${id}/volume`).then(({ data }) => data);
};

// Tolerant batch of per-token base VIP points/share (null for non-burnable ids),
// so a market/collected grid fetches once instead of one /volume call per card.
// Swallows errors to an empty map — callers treat a missing id as non-burnable.
export const fetchBurnTokenPoints = (ids: string[]): Promise<Record<string, number | null>> => {
  if (!ids.length) {
    return Promise.resolve({});
  }
  return axios
    .get<{ points: Record<string, number | null> }>(`/api/v1/burn/tokens/points`, { params: { ids: ids.join(',') } })
    .then(({ data }) => data.points ?? {})
    .catch(() => ({}));
};

// The caller's expected burn outcome for one share of `id`. Resolves to null when
// the token isn't burnable (400) or the caller is anonymous — callers treat null
// as "no burn action for this asset".
export const fetchBurnPreview = (id: string): Promise<IBurnPreview | null> => {
  return axios
    .get<IBurnPreview>(`/api/v1/burn/preview`, { params: { ids: id, amounts: 1 } })
    .then(({ data }) => data)
    .catch(() => null);
};

// The caller's current, token-independent burn standing (bonus multiplier + return
// chance). Resolves to null when anonymous, so callers can promote burning without
// its stats.
export const fetchBurnConfigUser = (): Promise<IBurnConfigUser | null> => {
  return axios
    .get<{ user: IBurnConfigUser }>(`/api/v1/burn/config`)
    .then(({ data }) => data.user)
    .catch(() => null);
};
